import { ShellAnnouncements } from '@opengov/analytics/shell';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { useCallback, useMemo, type ErrorInfo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEntity } from '../../contexts/EntityContext';
import { FeatureFlagKey, useFeatureFlag } from '../../hooks/useFeatureFlags';
import { useLoggerWithContext } from '../../services/logger/loggerContext';
import { reportAnnouncementProblemToFaro } from '../../services/announcements/reportToFaro';

/**
 * Statuspage component for Grants Management. PSP's routes attribute `/grants`
 * to `plt`, which the SDK's product → component map cannot tell apart from the
 * platform, so PSP adds it — on `/grants` pages only.
 */
const GRANTS_STATUS_COMPONENT = 'ssqftpy4t27q';

/** No customer selected yet: no placeholder values. */
const NO_PARAMS: Readonly<Record<string, string>> = {};

/**
 * The one banner slot on every PSP page, between the header and the content.
 *
 * Shows, in one stack (critical first, at most two):
 * - PSP's own `psp-announcement-*` LaunchDarkly JSON flags — platform-wide
 *   messages are posted here;
 * - the announcements of whichever module is mounted, handed over through
 *   `shell.analytics.setAnnouncements` (see `createModuleAnalytics`);
 * - incidents and maintenance from opengovstatus.com for the product the
 *   current path belongs to, plus OpenGov Platform — only while the
 *   `psp-ui-status-page-announcements` flag is on.
 *
 * No kill switch for the slot itself: each announcement is turned off through
 * its own flag. The status page has one switch, the flag above, because it is
 * the one source PSP does not author. It is off by default, as in the SDK, so a
 * LaunchDarkly outage hides status-page banners and leaves the rest.
 * Reads the LaunchDarkly client PSP already has — no new client, no new cost —
 * and `allFlags()` rather than `useFlags()`, whose keys are camelCased.
 *
 * Wrapped in its own error boundary, like the header's chrome: a banner that
 * fails to render disappears and is logged, and never takes the page with it.
 * (`onError` on `ShellAnnouncements` reports announcement problems, not render
 * errors.)
 */
export function PspAnnouncements() {
  const logger = useLoggerWithContext();
  const onBoundaryError = useCallback(
    (error: unknown, info: ErrorInfo) => {
      logger.error('Announcement banner failed to render', {
        error: error instanceof Error ? error.message : String(error),
        componentStack: info.componentStack,
      });
    },
    [logger]
  );

  return (
    <ErrorBoundary fallback={null} onError={onBoundaryError}>
      <PspAnnouncementStack />
    </ErrorBoundary>
  );
}

function PspAnnouncementStack() {
  const ldClient = useLDClient();
  const { pathname } = useLocation();
  // In-place navigation for same-origin banner links, the way the sidebar does
  // it: PSP's router moves, and `useNavigationBroadcast` tells the mounted
  // module. Without it the SDK leaves the link to the browser (a full reload).
  const navigate = useNavigate();
  const onGrants = pathname === '/grants' || pathname.startsWith('/grants/');
  // `{entity}` in a flag's link or routes — `/general-ledger/{entity}/…` — is the
  // active customer's slug, as in PSP URLs. The SDK would also take it from the
  // analytics identity; passing it here makes it work from the first render and
  // when analytics is off. No customer yet: such links are hidden, not broken.
  const { activeEntity } = useEntity();
  const entity = activeEntity?.friendlyId;
  const params = useMemo(() => (entity ? { entity } : NO_PARAMS), [entity]);

  // `false` must be passed, not an empty object: any object turns the status
  // page on. The SDK follows the value when the flag resolves after mount.
  const statusPageOn = useFeatureFlag(FeatureFlagKey.PSP_UI_STATUS_PAGE_ANNOUNCEMENTS);
  const statusPage = useMemo(
    () => (statusPageOn ? { components: onGrants ? [GRANTS_STATUS_COMPONENT] : [] } : false),
    [statusPageOn, onGrants]
  );

  return (
    <ShellAnnouncements
      client={ldClient}
      source="psp"
      onError={reportAnnouncementProblemToFaro}
      statusPage={statusPage}
      navigate={navigate}
      params={params}
      // Stated, not left to the SDK default: Layout relies on the banner
      // taking no height (see the comment where it is mounted).
      display="overlay"
    />
  );
}
